import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Camera, Upload } from "lucide-react";
import { Avatar } from "../Avatar";

/**
 * Self-service profile editor — used inside Dashboard for artists, buyers,
 * and art centers. Extra long-form fields (mission statement + about) surface
 * only for the `art_center` role.
 */
export const ProfileEditor = ({ user, onUpdated }) => {
  const isArtist = user?.role === "artist";
  const isCenter = user?.role === "art_center";
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    statement: user?.statement || "",
    country: user?.country || "",
    avatar_url: user?.avatar_url || "",
    instagram_handle: user?.instagram_handle || "",
    twitter_handle: user?.twitter_handle || "",
    facebook_url: user?.facebook_url || "",
    website_url: user?.website_url || "",
    mission_statement: user?.mission_statement || "",
    about: user?.about || "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const handleAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB for avatars)");
      e.target.value = "";
      return;
    }
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(f.type)) {
      toast.error("Please pick a PNG, JPEG, WebP or GIF image");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, avatar_url: data.url }));
      toast.success(isCenter ? "Logo uploaded" : "Avatar uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only send changed fields to keep PATCH minimal & avoid clobbering with defaults
      const payload = {};
      const editable = [
        "name", "bio", "statement", "country", "avatar_url",
        "instagram_handle", "twitter_handle", "facebook_url", "website_url",
      ];
      if (isCenter) editable.push("mission_statement", "about");
      editable.forEach((k) => {
        if ((form[k] ?? "") !== (user?.[k] ?? "")) payload[k] = form[k];
      });
      if (!Object.keys(payload).length) {
        toast.info("Nothing to update");
        setSaving(false);
        return;
      }
      const { data } = await api.patch("/users/me", payload);
      toast.success("Profile updated");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="profile-editor"
      onSubmit={handleSave}
      className="mt-10 border border-[#E5E0D8] bg-white p-6 md:p-8"
    >
      <p className="label-eyebrow text-[#1E3A8A]">— {isCenter ? "Hub profile" : "Profile"}</p>
      <h2 className="font-serif-display text-3xl mt-2 font-light text-[#111]">
        {isCenter ? "How the public sees your hub" : "How collectors see you"}
      </h2>

      <div className="mt-8 flex items-start gap-6 flex-wrap">
        <div className="relative">
          <Avatar src={form.avatar_url} name={form.name} size="xl" testid="profile-avatar-preview" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="avatar-upload-btn"
            className="absolute -bottom-2 -right-2 bg-[#1E3A8A] text-white p-2 rounded-full border-2 border-white hover:bg-[#162a66] transition-colors disabled:opacity-60"
            aria-label={isCenter ? "Upload hub logo" : "Upload avatar"}
          >
            {uploading ? (
              <Upload className="w-4 h-4 animate-pulse" />
            ) : (
              <Camera className="w-4 h-4" strokeWidth={2} />
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleAvatarPick}
            className="hidden"
            data-testid="avatar-file-input"
          />
          {isCenter && (
            <p className="mt-3 text-xs text-[#555] font-mono-aam max-w-[140px]">
              Hub logo — square works best.
            </p>
          )}
        </div>
        <div className="flex-1 min-w-[240px] space-y-4">
          <div>
            <label className="label-eyebrow text-[#555]">{isCenter ? "Hub name" : "Display name"}</label>
            <Input
              data-testid="profile-name"
              value={form.name}
              onChange={set("name")}
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Country</label>
            <Input
              data-testid="profile-country"
              value={form.country}
              onChange={set("country")}
              placeholder="e.g. Zimbabwe"
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
        </div>
      </div>

      {isArtist && (
        <>
          <div className="mt-8">
            <label className="label-eyebrow text-[#555]">Short bio</label>
            <Textarea
              data-testid="profile-bio"
              rows={3}
              maxLength={400}
              value={form.bio}
              onChange={set("bio")}
              placeholder="A one-paragraph introduction (max 400 chars)"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
            <p className="text-xs text-[#888] mt-1 font-mono-aam">
              {form.bio.length}/400
            </p>
          </div>
          <div className="mt-6">
            <label className="label-eyebrow text-[#555]">Artist statement</label>
            <Textarea
              data-testid="profile-statement"
              rows={5}
              maxLength={1200}
              value={form.statement}
              onChange={set("statement")}
              placeholder="What drives your practice? (max 1200 chars)"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
            <p className="text-xs text-[#888] mt-1 font-mono-aam">
              {form.statement.length}/1200
            </p>
          </div>
        </>
      )}

      {isCenter && (
        <>
          <div className="mt-8">
            <label className="label-eyebrow text-[#555]">Short intro (shown at the top of your public page)</label>
            <Textarea
              data-testid="profile-bio"
              rows={3}
              maxLength={400}
              value={form.bio}
              onChange={set("bio")}
              placeholder="A one-paragraph introduction to your hub (max 400 chars)"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
            <p className="text-xs text-[#888] mt-1 font-mono-aam">
              {form.bio.length}/400
            </p>
          </div>

          <div className="mt-6">
            <label className="label-eyebrow text-[#555]">Mission statement</label>
            <Textarea
              data-testid="profile-mission"
              rows={3}
              maxLength={400}
              value={form.mission_statement}
              onChange={set("mission_statement")}
              placeholder="What is your hub's mission? (max 400 chars)"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
            <p className="text-xs text-[#888] mt-1 font-mono-aam">
              {form.mission_statement.length}/400
            </p>
          </div>

          <div className="mt-6">
            <label className="label-eyebrow text-[#555]">
              About the hub — history, programs, values
            </label>
            <Textarea
              data-testid="profile-about"
              rows={8}
              maxLength={2000}
              value={form.about}
              onChange={set("about")}
              placeholder="Tell collectors and artists about your hub — when it was founded, the artists in residence, exhibitions, workshops, and what makes it unique. (max 2000 chars)"
              className="mt-2 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
            />
            <p className="text-xs text-[#888] mt-1 font-mono-aam">
              {form.about.length}/2000
            </p>
          </div>
        </>
      )}

      {/* Social media (any signed-in user) */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <label className="label-eyebrow text-[#555]">Instagram</label>
          <Input
            data-testid="profile-instagram"
            value={form.instagram_handle}
            onChange={set("instagram_handle")}
            placeholder="@yourhandle"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
        <div>
          <label className="label-eyebrow text-[#555]">X (Twitter)</label>
          <Input
            data-testid="profile-twitter"
            value={form.twitter_handle}
            onChange={set("twitter_handle")}
            placeholder="@yourhandle"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
        <div>
          <label className="label-eyebrow text-[#555]">Facebook URL</label>
          <Input
            data-testid="profile-facebook"
            value={form.facebook_url}
            onChange={set("facebook_url")}
            placeholder="https://facebook.com/…"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
        <div>
          <label className="label-eyebrow text-[#555]">Website</label>
          <Input
            data-testid="profile-website"
            value={form.website_url}
            onChange={set("website_url")}
            placeholder="https://…"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between gap-3">
        <p className="text-xs text-[#555]">
          Profile changes are visible {isCenter ? "on your public hub page" : "to collectors"} immediately.
        </p>
        <Button
          type="submit"
          disabled={saving || uploading}
          data-testid="save-profile-btn"
          className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-8 py-3 h-auto label-eyebrow disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </form>
  );
};

export default ProfileEditor;
